import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { Select, Option, Input } from '@material-tailwind/react';
import { useGetBranches } from '../../../custom-hooks/use-branches';
import { useGetBuildings } from '../../../custom-hooks/use-buildings';
import { GetFloors } from '../../../services/backend';

// Cascading Branch -> Building -> Floor selects + a free-text "Space" label (old sheet
// parity — hallways, rooftops, and other areas that aren't modeled as real spaces).
//
// Floors are fetched here directly (keyed by buildingId) instead of via the shared
// useGetFloors hook, which caches every call under the single query key 'floors'
// regardless of buildingId — fine for pages that only ever look at one building, but
// it means switching buildings in a cascading picker like this one wouldn't refetch.
const LocationPicker = ({
    branch, building, floor, space, onChange, disabled = false,
    // Opt-in only — default keeps existing callers (Maintenance) byte-identical.
    // When there's exactly one branch, skip making the user pick it.
    autoSelectSingleBranch = false,
    showSpace = true,
}) => {
    const { data: branches = [] } = useGetBranches();
    const { data: allBuildings = [] } = useGetBuildings();

    const buildings = allBuildings.filter((b) => String(b.branch?._id || b.branch) === String(branch));

    const { data: floors = [] } = useQuery(
        ['maintenance-location-floors', building],
        () => GetFloors(building).then((res) => res?.data?.floors || []),
        { enabled: !!building, staleTime: 5 * 60 * 1000 }
    );

    const singleBranchOnly = autoSelectSingleBranch && branches.length === 1;

    useEffect(() => {
        if (singleBranchOnly && !branch) onChange({ branch: branches[0]._id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleBranchOnly, branch, branches]);

    const branchLabel = branches.find((b) => b._id === branch)?.branchName || '';
    const buildingLabel = buildings.find((b) => b._id === building)?.name || '';
    const floorLabel = floors.find((f) => f._id === floor) ? `Floor ${floors.find((f) => f._id === floor).number}` : '';

    return (
        <div className="flex flex-col gap-3">
            {singleBranchOnly && branchLabel && (
                <p className="text-xs font-medium text-[#6B675F]">Branch: <span className="text-inv-ink">{branchLabel}</span></p>
            )}
            <div className={`grid grid-cols-1 gap-3 ${singleBranchOnly ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                {!singleBranchOnly && (
                    <Select
                        label="Branch *"
                        value={branch}
                        disabled={disabled}
                        onChange={(v) => onChange({ branch: v, building: '', floor: '' })}
                        selected={() => branchLabel}
                    >
                        {branches.map((b) => <Option key={b._id} value={b._id}>{b.branchName}</Option>)}
                    </Select>
                )}

                <Select
                    label="Building *"
                    value={building}
                    disabled={disabled || !branch}
                    onChange={(v) => onChange({ building: v, floor: '' })}
                    selected={() => buildingLabel}
                >
                    {buildings.map((b) => <Option key={b._id} value={b._id}>{b.name}</Option>)}
                </Select>

                <Select
                    label="Floor *"
                    value={floor}
                    disabled={disabled || !building}
                    onChange={(v) => onChange({ floor: v })}
                    selected={() => floorLabel}
                >
                    {floors.map((f) => <Option key={f._id} value={f._id}>{`Floor ${f.number}${f.floorCode ? ` (${f.floorCode})` : ''}`}</Option>)}
                </Select>
            </div>

            {showSpace && (
                <div>
                    {/* Short label, examples underneath. Material Tailwind's floating label
                        can't wrap or shrink, so the full "e.g. Meeting Room A, Hallway,
                        Rooftop" version overflowed the field on a narrow screen. */}
                    <Input
                        label="Space (optional)"
                        value={space}
                        disabled={disabled}
                        onChange={(e) => onChange({ space: e.target.value })}
                    />
                    <p className="mt-1.5 text-xs text-inv-muted">
                        e.g. Meeting Room A, Hallway, Rooftop
                    </p>
                </div>
            )}
        </div>
    );
};

export default LocationPicker;
